/* eslint-disable @skyscanner-internal/skyscanner-dates/no-date-fns-format */
// Disabled eslint-plugin-skyscanner-dates as part of this decision record https://skyscanner.atlassian.net/l/cp/oU9K9r4S
import { addOrModifyQueryParam } from '@web-platform/global-components-utils/urls';
import { addDays } from 'date-fns/addDays';
import { format } from 'date-fns/format';
import { parseISO } from 'date-fns/parseISO';

import type { SearchContext } from '../types';
import type { GenerateUrl, UmsGenerateUrlResult } from '@web-platform/global-components-types/ums';

const buildCarHireUrl = (baseUrl: string, context: SearchContext = {}) => {
  let url = baseUrl;

  url = addOrModifyQueryParam(url, 'pick_up', context.destinationAirport);

  if (context.outboundDate !== 'wholeyear') {
    url = addOrModifyQueryParam(url, 'pick_up_date', context.outboundDate);
  }

  if (context.tripType === 'return') {
    url = addOrModifyQueryParam(url, 'drop_off', context.destinationAirport);
    if (context.inboundDate !== 'wholeyear') {
      url = addOrModifyQueryParam(url, 'drop_off_date', context.inboundDate);
    }
  }
  const queryKey = context.destinationId ? 's-f_iplace' : 'entity_id';

  url = addOrModifyQueryParam(url, queryKey, context.destinationId || context.entityId);

  return url;
};

const addDaystoDate = (days: number, dateString?: string) => {
  if (dateString) {
    let currentDate = parseISO(dateString);

    currentDate = addDays(currentDate, days);

    const iso = format(currentDate, 'yyyy-MM-dd');
    return `${iso}`;
  }
  return undefined;
};

const buildHotelsUrl = (baseUrl: string, context: SearchContext = {}) => {
  let url = baseUrl;

  url = addOrModifyQueryParam(url, 'na', context.adultsV2);

  if (context.outboundDate !== 'wholeyear') {
    url = addOrModifyQueryParam(url, 'sd', context.outboundDate);

    if (context.tripType === 'return') {
      url = addOrModifyQueryParam(url, 'ed', context.inboundDate);
    } else {
      url = addOrModifyQueryParam(url, 'ed', addDaystoDate(1, context.outboundDate));
    }
  }

  url = addOrModifyQueryParam(url, 's-f_iplace', context.destinationId);
  return url;
};

const DEFAULT = {
  URLS: {
    flights: '/flights',
    hotels: '/hotels',
    carHire: '/carhire',
    packages: '/destinations/package-holidays',
  },
};
const PAGE_TYPE = {
  FLIGHTS: 'flights-home',
  CARHIRE: 'car-hire-home',
  HOTELS: 'hotels-home',
  PACKAGES: 'package-holidays-home',
};
const pages = {
  flights: {
    pageType: PAGE_TYPE.FLIGHTS,
    defaultUrl: DEFAULT.URLS.flights,
  },
  carHire: {
    pageType: PAGE_TYPE.CARHIRE,
    defaultUrl: DEFAULT.URLS.carHire,
  },
  hotels: {
    pageType: PAGE_TYPE.HOTELS,
    defaultUrl: DEFAULT.URLS.hotels,
  },
  packages: {
    pageType: PAGE_TYPE.PACKAGES,
    defaultUrl: DEFAULT.URLS.packages,
  },
};

const getUrl = async (
  generateUrl: GenerateUrl,
  market: string,
  pagetype: string,
  locale: string,
  fallback: string,
) => {
  try {
    return await generateUrl(market, pagetype, locale, {});
  } catch (e) {
    return { url: fallback } as UmsGenerateUrlResult;
  }
};

/**
 * Get the three URLs for the Flights, Hotels, and Car-Hire tab buttons
 * @public
 * @param generateUrl - Instance of ums.generateUrl provided by consumer
 * @param locale - The locale to use for the URL generation, defaults to 'en-GB'
 * @param market - The market to use for the URL generation, defaults to 'uk'
 */
const getTabUrls = async (
  generateUrl: GenerateUrl,
  locale: string = 'en-GB',
  market: string = 'uk',
): Promise<{ flights: string; carHire: string; hotels: string; packages: string }> => {
  const pageUrlsPromises = [pages.flights, pages.carHire, pages.hotels, pages.packages].map(
    (page) =>
      getUrl(
        generateUrl,
        market.toLowerCase(),
        page.pageType,
        locale.toLowerCase(),
        page.defaultUrl,
      ).then(({ url }) => url),
  );

  const [flights, carHire, hotels, packages] = await Promise.all(pageUrlsPromises);

  return {
    flights,
    carHire,
    hotels,
    packages,
  };
};

export { buildCarHireUrl, buildHotelsUrl, addDaystoDate, getTabUrls };
